import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Stack,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Paper,
  Tooltip,
  Divider,
  Chip,
  InputAdornment,
  Avatar,
  alpha
} from '@mui/material';
import {
  Add,
  Delete,
  Edit,
  CloudUpload,
  Download,
  FilterList,
  FolderOpen,
  Close,
  PictureAsPdf,
  Search,
  Public,
  Business,
  Visibility,
  Description
} from '@mui/icons-material';
import apiClient from '../../../../../api/apiClient';
import { useGetCompaniesQuery } from '../../../../../services/orgApi';

const formInputStyle = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    transition: 'all 0.2s ease-in-out',
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#7522D1',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#7522D1',
      borderWidth: '2px',
    },
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#7522D1',
  },
};

const OnboardingTemplates = () => {
  const { data: companies } = useGetCompaniesQuery();
  const [entities, setEntities] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState('everything');
  const [searchQuery, setSearchQuery] = useState('');

  const [formData, setFormData] = useState({
    id: '',
    entityId: '',
    templateName: '',
    label: '',
    description: '',
    isActive: true,
    templateFile: null
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (companies && companies.length > 0) {
      setEntities(companies[0].entities || []);
    }
  }, [companies]);

  const fetchTemplates = async () => {
    setIsLoading(true);
    try {
      let url = '/onboarding/templates/entity/null';

      if (selectedEntity === 'everything') {
        url = '/onboarding/templates/entity/all';
      } else if (selectedEntity === 'global') {
        url = '/onboarding/templates/entity/null';
      } else {
        url = `/onboarding/templates/entity/${selectedEntity}`;
      }

      const response = await apiClient.get(url);
      if (response.data.success) {
        setTemplates(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, [selectedEntity]);

  const handleOpenDialog = (template = null) => {
    if (template) {
      setFormData({
        id: template.id,
        entityId: template.entityId || '',
        templateName: template.templateName,
        label: template.label,
        description: template.description || '',
        isActive: template.isActive,
        templateFile: null
      });
    } else {
      setFormData({
        id: '',
        entityId: selectedEntity === 'everything' || selectedEntity === 'global' ? '' : selectedEntity,
        templateName: '',
        label: '',
        description: '',
        isActive: true,
        templateFile: null
      });
    }
    setErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e) => {
    setFormData(prev => ({
      ...prev,
      templateFile: e.target.files[0]
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.templateName) newErrors.templateName = 'Internal name is required';
    if (!formData.label) newErrors.label = 'Display label is required';
    if (!formData.id && !formData.templateFile) newErrors.templateFile = 'File is required for new templates';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const data = new FormData();
      Object.keys(formData).forEach(key => {
        if (key === 'templateFile' && formData[key]) {
          data.append(key, formData[key]);
        } else if (key !== 'templateFile') {
          data.append(key, formData[key]);
        }
      });

      const response = await apiClient.post('/onboarding/templates', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        handleCloseDialog();
        fetchTemplates();
      }
    } catch (error) {
      console.error('Error saving template:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      const response = await apiClient.delete(`/onboarding/templates/${id}`);
      if (response.data.success) {
        fetchTemplates();
      }
    } catch (error) {
      console.error('Error deleting template:', error);
    }
  };

  const getEntityName = (id) => {
    if (!id) return 'Global / All Entities';
    const entity = entities.find(e => e.id === id);
    return entity ? entity.name : 'Unknown Entity';
  };

  const isImage = (url) => {
    if (!url) return false;
    return /\.(jpg|jpeg|png|webp|avif|gif|svg)$/.test(url.toLowerCase());
  };

  const filteredTemplates = templates.filter(t =>
    t.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.templateName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    getEntityName(t.entityId).toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Box sx={{ bgcolor: '#F9FAFB', minHeight: '100%', p: 3, borderRadius: 4 }}>
      {/* Header Area */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#111827" sx={{ letterSpacing: '-0.02em' }}>
            Template Management
          </Typography>
          <Typography variant="body2" color="#6B7280" fontWeight={500}>
            Configure and assign onboarding documents for your organization.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
          sx={{
            bgcolor: '#7522D1',
            color: '#FFFFFF',
            px: 3,
            py: 1.2,
            borderRadius: 2.5,
            textTransform: 'none',
            fontWeight: 700,
            boxShadow: '0 4px 12px rgba(117, 34, 209, 0.2)',
            '&:hover': { bgcolor: '#6A1DB0', transform: 'translateY(-1px)' },
            transition: 'all 0.2s'
          }}
        >
          Add New Template
        </Button>
      </Box>

      {/* Control Bar */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 4,
          borderRadius: 4,
          border: '1px solid #E5E7EB',
          bgcolor: '#FFFFFF',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'center',
          gap: 2.5
        }}
      >
        <FormControl size="small" sx={{ minWidth: 240, ...formInputStyle }}>
          <InputLabel>Filter by Scope</InputLabel>
          <Select
            value={selectedEntity}
            label="Filter by Scope"
            onChange={(e) => setSelectedEntity(e.target.value)}
            startAdornment={<FilterList sx={{ mr: 1, color: '#9CA3AF' }} />}
            sx={{ borderRadius: '10px', fontWeight: 600 }}
          >
            <MenuItem value="everything">
              <Stack direction="row" spacing={1} alignItems="center">
                <FolderOpen fontSize="small" sx={{ color: '#7522D1' }} />
                <Typography variant="body2" fontWeight={600}>All Templates</Typography>
              </Stack>
            </MenuItem>
            <MenuItem value="global">
              <Stack direction="row" spacing={1} alignItems="center">
                <Public fontSize="small" sx={{ color: '#10B981' }} />
                <Typography variant="body2" fontWeight={600}>Global / Default</Typography>
              </Stack>
            </MenuItem>
            <Divider />
            {entities.map(entity => (
              <MenuItem key={entity.id} value={entity.id}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Business fontSize="small" sx={{ color: '#6366F1' }} />
                  <Typography variant="body2" fontWeight={600}>{entity.name}</Typography>
                </Stack>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          fullWidth
          size="small"
          placeholder="Search by name, ID, or entity..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: '#9CA3AF' }} />
              </InputAdornment>
            ),
            sx: { borderRadius: '10px', bgcolor: '#F9FAFB' }
          }}
          sx={formInputStyle}
        />
      </Paper>

      {/* Grid Display */}
      {isLoading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 12, gap: 2 }}>
          <CircularProgress size={40} thickness={4} sx={{ color: '#7522D1' }} />
          <Typography variant="body2" color="text.secondary" fontWeight={600}>Fetching templates...</Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {filteredTemplates.length === 0 ? (
            <Grid item xs={12}>
              <Box sx={{
                py: 12,
                textAlign: 'center',
                bgcolor: '#FFFFFF',
                borderRadius: 5,
                border: '1px solid #E5E7EB',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}>
                <Box sx={{ p: 2, bgcolor: '#F3F4F6', borderRadius: '50%', mb: 2 }}>
                  <FolderOpen sx={{ fontSize: 48, color: '#9CA3AF' }} />
                </Box>
                <Typography variant="h6" fontWeight={700} color="#374151">No templates found</Typography>
                <Typography variant="body2" color="#6B7280" sx={{ mt: 1, maxWidth: 300 }}>
                  Adjust your filters or add a new template to get started.
                </Typography>
              </Box>
            </Grid>
          ) : (
            filteredTemplates.map((template) => (
              <Grid item xs={12} sm={6} md={4} key={template.id}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    borderRadius: '16px',
                    border: '1px solid rgba(117, 34, 209, 0.08)',
                    bgcolor: '#FFFFFF',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      borderColor: '#7522D1',
                      boxShadow: '0 12px 32px rgba(117, 34, 209, 0.1)',
                      transform: 'translateY(-4px)'
                    }
                  }}
                >
                  <CardContent sx={{ p: 3, flex: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2.5 }}>
                      {isImage(template.fileUrl) ? (
                        <Avatar
                          src={template.fileUrl}
                          variant="rounded"
                          sx={{ width: 56, height: 56, bgcolor: '#F3F4F6', border: '1px solid #E5E7EB' }}
                        >
                          <Description sx={{ color: '#9CA3AF' }} />
                        </Avatar>
                      ) : (
                        <Box sx={{
                          p: 1.5,
                          borderRadius: 2.5,
                          bgcolor: template.isActive ? '#F5F3FF' : '#F9FAFB',
                          color: template.isActive ? '#7522D1' : '#6B7280',
                          display: 'flex'
                        }}>
                          <PictureAsPdf sx={{ fontSize: 32 }} />
                        </Box>
                      )}

                      <Stack direction="row" spacing={1}>
                        {!template.isActive && (
                          <Chip label="Inactive" size="small" sx={{ bgcolor: '#FEF2F2', color: '#B91C1C', fontWeight: 700, fontSize: '0.7rem' }} />
                        )}
                        <Chip
                          label={template.entityId ? "Entity-Specific" : "Global Scope"}
                          size="small"
                          variant="outlined"
                          sx={{
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            borderColor: template.entityId ? '#E0E7FF' : '#D1FAE5',
                            bgcolor: template.entityId ? '#EEF2FF' : '#F0FDF4',
                            color: template.entityId ? '#4338CA' : '#15803D'
                          }}
                        />
                      </Stack>
                    </Box>

                    <Typography variant="h6" fontWeight={800} color="#111827" sx={{ mb: 0.5, lineHeight: 1.3 }}>
                      {template.label}
                    </Typography>
                    <Typography variant="caption" color="#6B7280" fontWeight={600} display="block" sx={{ mb: 2 }}>
                      System ID: {template.templateName}
                    </Typography>

                    <Box sx={{ mb: 2.5, p: 1.5, bgcolor: '#F9FAFB', borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={800} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <Business fontSize="inherit" /> ASSIGNED TO
                      </Typography>
                      <Typography variant="body2" fontWeight={700} color="#4B5563">
                        {getEntityName(template.entityId)}
                      </Typography>
                    </Box>

                    <Typography
                      variant="body2"
                      color="#6B7280"
                      sx={{
                        mb: 3,
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: 1.6,
                        height: 60
                      }}
                    >
                      {template.description || 'No instructions or description provided for this document.'}
                    </Typography>

                    <Divider sx={{ mb: 2, borderStyle: 'dashed' }} />

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="View File">
                          <IconButton
                            size="small"
                            onClick={() => window.open(template.fileUrl, '_blank')}
                            sx={{ color: '#7522D1', bgcolor: '#F5F3FF', '&:hover': { bgcolor: '#EDE9FE' } }}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download">
                          <IconButton
                            size="small"
                            onClick={() => {
                              const link = document.createElement('a');
                              link.href = template.fileUrl;
                              link.download = `${template.label}${template.fileUrl.substring(template.fileUrl.lastIndexOf('.'))}`;
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                            }}
                            sx={{ color: '#4B5563', '&:hover': { color: '#7522D1', bgcolor: '#F5F3FF' } }}
                          >
                            <Download fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(template)}
                          sx={{ color: '#4B5563', '&:hover': { color: '#7522D1', bgcolor: '#F5F3FF' } }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(template.id)}
                          sx={{ color: '#4B5563', '&:hover': { color: '#EF4444', bgcolor: '#FEF2F2' } }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))
          )}
        </Grid>
      )}

      {/* Editor Modal */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4, boxShadow: '0 25px 50px -12px rgba(0,0,0,0.15)' } }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #F3F4F6', px: 4, py: 2.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight={800} sx={{ letterSpacing: '-0.01em' }}>
              {formData.id ? 'Modify Template' : 'Deploy New Template'}
            </Typography>
            <IconButton onClick={handleCloseDialog} size="small" sx={{ bgcolor: '#F3F4F6' }}><Close /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Stack spacing={3.5} sx={{ mt: 1.5 }}>
            <FormControl fullWidth sx={formInputStyle}>
              <InputLabel>Entity Assignment Scope</InputLabel>
              <Select
                name="entityId"
                value={formData.entityId}
                label="Entity Assignment Scope"
                onChange={handleInputChange}
                sx={{ borderRadius: '10px' }}
              >
                <MenuItem value=""><em>Global Scope (All Entities)</em></MenuItem>
                {entities.map(entity => (
                  <MenuItem key={entity.id} value={entity.id}>{entity.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 2.5 }}>
              <TextField
                fullWidth
                label="Friendly Label"
                name="label"
                value={formData.label}
                onChange={handleInputChange}
                error={!!errors.label}
                helperText={errors.label}
                placeholder="e.g. Identity Proof"
                sx={formInputStyle}
              />
              <TextField
                fullWidth
                label="Internal ID"
                name="templateName"
                value={formData.templateName}
                onChange={handleInputChange}
                error={!!errors.templateName}
                helperText={errors.templateName}
                placeholder="e.g. id_proof_v2"
                sx={formInputStyle}
              />
            </Box>

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Onboarding Instructions"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Tell candidates what they need to do..."
              sx={formInputStyle}
            />

            <Box sx={{
              p: 4,
              border: '2px dashed #D1D5DB',
              borderRadius: 3,
              textAlign: 'center',
              bgcolor: '#F9FAFB',
              transition: 'all 0.2s',
              '&:hover': { borderColor: '#7522D1', bgcolor: '#F5F3FF' }
            }}>
              <input
                accept=".pdf,.doc,.docx,image/*"
                style={{ display: 'none' }}
                id="template-upload-modal"
                type="file"
                onChange={handleFileChange}
              />
              <label htmlFor="template-upload-modal">
                <Box sx={{ cursor: 'pointer' }}>
                  <CloudUpload sx={{ fontSize: 40, color: '#9CA3AF', mb: 1.5 }} />
                  <Typography variant="subtitle2" fontWeight={800} color="#374151">
                    {formData.templateFile ? formData.templateFile.name : 'Select document file'}
                  </Typography>
                  <Typography variant="caption" color="#6B7280" display="block" sx={{ mt: 0.5 }}>
                    Accepted: PDF, DOCX, JPG, PNG (Max 4MB)
                  </Typography>
                </Box>
              </label>
              {errors.templateFile && (
                <Typography color="error" variant="caption" fontWeight={600} sx={{ mt: 1, display: 'block' }}>
                  {errors.templateFile}
                </Typography>
              )}
            </Box>

            <Stack direction="row" spacing={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                    sx={{ color: '#7522D1', '&.Mui-checked': { color: '#7522D1' } }}
                  />
                }
                label={<Typography variant="body2" fontWeight={700} color="#374151">Active</Typography>}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #F3F4F6', px: 4, py: 3, gap: 1.5 }}>
          <Button onClick={handleCloseDialog} sx={{ textTransform: 'none', color: '#6B7280', fontWeight: 700 }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting}
            sx={{
              bgcolor: '#7522D1',
              borderRadius: 2.5,
              textTransform: 'none',
              px: 5,
              py: 1,
              fontWeight: 700,
              boxShadow: 'none',
              '&:hover': { bgcolor: '#6A1DB0', boxShadow: 'none' }
            }}
          >
            {isSubmitting ? <CircularProgress size={20} sx={{ color: '#FFFFFF' }} /> : (formData.id ? 'Save Changes' : 'Publish Template')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OnboardingTemplates;
